/** Specifically handles the logic and state for adding a subtask to an existing parent task. */
// @ts-check
import { useCallback } from 'react';
import { useTasksContext } from '@/features/tasks/context/TasksContext.def';
import useAddTask from '@/features/tasks/hooks/mutations/use-task-add';
import useNewTaskLogic from '@/features/tasks/hooks/mutations/use-task-new-logic';
import { executeSaveSubtask } from '@/features/tasks/utils/mutations/taskAddSubtaskUtils';

/**
 * @param {Task | undefined} parentTask
 * @param {Task[] | undefined} tasks
 * @param {((task: Task) => void) | undefined} onUpdate
 * @returns {{ handleSaveSubtask: (subtaskData: Task | string) => Promise<string | null | undefined> }}
 */
const useAddSubtask = (parentTask, tasks, onUpdate) => {
  const { allTasks, tasksMap, user } = useTasksContext();
  const { addTask } = useAddTask();
  const { augmentNewTaskWithDefaults } = useNewTaskLogic();

  const handleSaveSubtask = /** @type {(subtaskData: Task | string) => Promise<string | null | undefined>} */ (
    useCallback(
      async (subtaskData) => {
        return executeSaveSubtask(
          parentTask,
          subtaskData,
          tasks || allTasks,
          addTask,
          augmentNewTaskWithDefaults,
          tasksMap,
          onUpdate,
          user?.uid,
        );
      },
      [
        parentTask,
        tasks,
        allTasks,
        addTask,
        augmentNewTaskWithDefaults,
        tasksMap,
        onUpdate,
        user,
      ],
    )
  );

  return { handleSaveSubtask };
};

export default useAddSubtask;
